import { DatePickerInput, Flex, Input, SaveText } from '#atoms';
import { PATH } from '#const/path';
import useCreateLicenseMutation from '#hooks/queries/onboard/useCreateLicenseMutation';
import { useToast } from '#providers/ToastProvider';
import { PrevHeaderBarWithTitleTemplate } from '#templates';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { palette } from 'src/libs/styled-components/palette';
import { useLicenseFormState } from '../OnBoardSearchLicense/OnBoardSearchLicense.hooks';
import * as S from './styles';

type Inputs = {
  gotAt: Date;
  name: string;
  issuer?: string;
  licenseIssuerId?: string;
};

export function OnBoardCreateLicense() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { licenseFormState, setLicenseFormState } = useLicenseFormState();
  const { mutateAsync: createLicense, isLoading } = useCreateLicenseMutation();
  const { control, handleSubmit, watch, setValue } = useForm<Inputs>({
    mode: 'onSubmit',
    defaultValues: {}
  });

  const [name, issuer, gotAt] = watch(['name', 'issuer', 'gotAt']);

  /*
    자격증이 존재할 경우 '자격증명', '발행처' 채우기 (name, issuer, licenseIssuerId)
    자격증이 존재하지 않을 경우 '자격증명' 등록 (name, issuer)
  */
  const onSubmit = async (data: Inputs) => {
    try {
      await createLicense(data);
      navigate(-1);
      setLicenseFormState({
        issuer: undefined,
        licenseIssuerId: undefined,
        name: undefined
      });
    } catch (err: any) {
      showToast({
        type: 'fail',
        message: '자격증 등록에 실패했습니다.'
      });
    }
  };

  useEffect(() => {
    if (licenseFormState.name) {
      setValue('name', licenseFormState.name);
    }
    if (licenseFormState.licenseIssuerId) {
      setValue('licenseIssuerId', licenseFormState.licenseIssuerId);
    }
    if (licenseFormState.issuer) {
      setValue('issuer', licenseFormState.issuer);
    }
  }, [licenseFormState]);

  return (
    <PrevHeaderBarWithTitleTemplate
      title="자격증"
      hasBorder={false}
      ContainerStyle={{ position: 'relative' }}
      defaultBackground
      rightContents={
        <SaveText
          disabled={!(name && issuer && gotAt) || isLoading}
          text="저장"
          onClick={handleSubmit(onSubmit)}
        />
      }
    >
      <S.Wrapper>
        <Flex direction="column" gap={32}>
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Input
                labelText="자격증명"
                placeholder="자격증명을 입력해 주세요"
                type="text"
                required
                value={field.value}
                onClick={() => navigate(PATH.ONBOARD_LICENSE_SEARCH)}
                textInputStyle={{
                  height: '54px',
                  borderRadius: '8px',
                  border: `1px solid ${palette.gray400}`
                }}
                textInputPlaceholderStyle={{
                  fontWeight: '500',
                  color: 'rgb(92, 87, 107)'
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="issuer"
            render={({ field }) => (
              <Input
                labelText="발행처"
                placeholder="발행처를 입력해 주세요"
                type="text"
                required
                textInputStyle={{
                  height: '54px',
                  borderRadius: '8px',
                  border: `1px solid ${palette.gray400}`
                }}
                textInputPlaceholderStyle={{
                  fontWeight: '500',
                  color: 'rgb(92, 87, 107)'
                }}
                {...field}
              />
            )}
          />
          <Flex direction="column" gap={16}>
            <Controller
              control={control}
              name="gotAt"
              render={({ field }) => (
                <DatePickerInput
                  label="취득 연월"
                  required
                  fullWidth
                  title="취득 연월 선택"
                  placeholder={
                    field.value ? undefined : '취득 연월을 입력해 주세요'
                  }
                  date={field.value}
                  onChange={field.onChange}
                  onlyYearMonth
                />
              )}
            />
          </Flex>
        </Flex>
      </S.Wrapper>
    </PrevHeaderBarWithTitleTemplate>
  );
}
